import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import { RefreshOutlined, DownloadOutlined } from '@mui/icons-material';
import api from '../../../api/axios';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`log-tabpanel-${index}`}
      aria-labelledby={`log-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface EmailLog {
  id: number;
  status: 'attempt' | 'success' | 'error';
  message_id: string | null;
  recipient: string;
  subject: string;
  details: string | null;
  created_at: string;
}

interface AuthLog {
  id: number;
  username: string;
  ip_address: string;
  user_agent: string;
  status: 'success' | 'failed';
  reason: string | null;
  attempt_time: string;
}

interface SystemLog {
  logs: string;
  type: string;
}

const LogViewer: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [authLogs, setAuthLogs] = useState<AuthLog[]>([]);
  const [systemLogs, setSystemLogs] = useState<SystemLog | null>(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [systemLoading, setSystemLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [logType, setLogType] = useState<'notification' | 'api' | 'all'>('notification');
  const [statusFilter, setStatusFilter] = useState<'' | 'attempt' | 'success' | 'error'>('');
  const [authStatusFilter, setAuthStatusFilter] = useState<'' | 'success' | 'failed'>('');
  const [authUsernameFilter, setAuthUsernameFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalEmailLogs, setTotalEmailLogs] = useState(0);
  const [totalAuthLogs, setTotalAuthLogs] = useState(0);
  const [logLines, setLogLines] = useState(100);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const fetchEmailLogs = async () => {
    setEmailLoading(true);
    setError(null);

    try {
      const params: any = {
        limit: rowsPerPage,
        offset: page * rowsPerPage
      };

      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await api.get('/api/admin/logs/email', { params });
      setEmailLogs(response.data.logs);
      setTotalEmailLogs(response.data.pagination.total);
    } catch (err: any) {
      console.error('Error fetching email logs:', err);
      setError(err.response?.data?.error || 'Failed to fetch email logs');
      setSnackbarOpen(true);
    } finally {
      setEmailLoading(false);
    }
  };

  const fetchAuthLogs = async () => {
    setAuthLoading(true);
    setError(null);

    try {
      const params: any = {
        limit: rowsPerPage,
        offset: page * rowsPerPage
      };

      if (authStatusFilter) {
        params.status = authStatusFilter;
      }

      if (authUsernameFilter) {
        params.username = authUsernameFilter;
      }

      const response = await api.get('/api/admin/logs/auth', { params });
      setAuthLogs(response.data.logs);
      setTotalAuthLogs(response.data.pagination.total);
    } catch (err: any) {
      console.error('Error fetching authentication logs:', err);
      setError(err.response?.data?.error || 'Failed to fetch authentication logs');
      setSnackbarOpen(true);
    } finally {
      setAuthLoading(false);
    }
  };

  const fetchSystemLogs = async () => {
    setSystemLoading(true);
    setError(null);

    try {
      const response = await api.get('/api/admin/logs/system', {
        params: {
          type: logType,
          lines: logLines
        }
      });
      setSystemLogs(response.data);
    } catch (err: any) {
      console.error('Error fetching system logs:', err);
      setError(err.response?.data?.error || 'Failed to fetch system logs');
      setSnackbarOpen(true);
    } finally {
      setSystemLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 0) {
      fetchEmailLogs();
    } else if (activeTab === 1) {
      fetchAuthLogs();
    } else {
      fetchSystemLogs();
    }
  }, [activeTab, page, rowsPerPage, statusFilter, authStatusFilter, authUsernameFilter, logType]);

  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleAuthStatusFilterChange = (event: any) => {
    setAuthStatusFilter(event.target.value);
    setPage(0);
  };

  const handleAuthUsernameFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAuthUsernameFilter(event.target.value);
  };

  const handleAuthFilterSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    fetchAuthLogs();
  };

  const handleLogTypeChange = (event: any) => {
    setLogType(event.target.value);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeLogLines = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLogLines(parseInt(event.target.value, 10));
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const downloadLogs = () => {
    if (activeTab === 0 && emailLogs.length > 0) {
      // Download email logs as JSON
      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(emailLogs, null, 2));
      const downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute("href", dataStr);
      downloadAnchorNode.setAttribute("download", `email_logs_${new Date().toISOString()}.json`);
      document.body.appendChild(downloadAnchorNode);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
    } else if (activeTab === 1 && authLogs.length > 0) {
      // Download authentication logs as JSON
      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(authLogs, null, 2));
      const downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute("href", dataStr);
      downloadAnchorNode.setAttribute("download", `auth_logs_${new Date().toISOString()}.json`);
      document.body.appendChild(downloadAnchorNode);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
    } else if (activeTab === 2 && systemLogs) {
      // Download system logs as text
      const dataStr = "data:text/plain;charset=utf-8," + encodeURIComponent(systemLogs.logs);
      const downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute("href", dataStr);
      downloadAnchorNode.setAttribute("download", `system_logs_${logType}_${new Date().toISOString()}.txt`);
      document.body.appendChild(downloadAnchorNode);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'attempt':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper elevation={3} sx={{ mb: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', p: 1, display: 'flex', justifyContent: 'space-between' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="log tabs">
            <Tab label="Email Notifications" />
            <Tab label="Authentication Logs" />
            <Tab label="System Logs" />
          </Tabs>
          <Box>
            <Button 
              startIcon={<RefreshOutlined />} 
              onClick={() => {
                if (activeTab === 0) fetchEmailLogs();
                else if (activeTab === 1) fetchAuthLogs();
                else fetchSystemLogs();
              }}
              sx={{ mr: 1 }}
            >
              Refresh
            </Button>
            <Button 
              startIcon={<DownloadOutlined />} 
              onClick={downloadLogs}
              disabled={(activeTab === 0 && emailLogs.length === 0) || 
                      (activeTab === 1 && authLogs.length === 0) || 
                      (activeTab === 2 && !systemLogs)}
            >
              Download
            </Button>
          </Box>
        </Box>
        
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ mb: 2 }}>
            <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="status-filter-label">Status Filter</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                label="Status Filter"
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="attempt">Attempts</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="error">Errors</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          {emailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : emailLogs.length === 0 ? (
            <Typography variant="body1" sx={{ textAlign: 'center', p: 3 }}>
              No email logs found.
            </Typography>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table sx={{ minWidth: 650 }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Recipient</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {emailLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{log.id}</TableCell>
                        <TableCell>
                          <Chip 
                            label={log.status} 
                            color={getStatusColor(log.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{log.recipient}</TableCell>
                        <TableCell>{log.subject}</TableCell>
                        <TableCell>{new Date(log.created_at).toLocaleString()}</TableCell>
                        <TableCell>
                          {log.details ? (
                            <Box
                              component="div"
                              sx={{
                                maxHeight: 100,
                                overflow: 'auto',
                                whiteSpace: 'pre-wrap',
                                fontSize: '0.8rem',
                                bgcolor: 'background.paper',
                                p: 1,
                                borderRadius: 1,
                                border: '1px solid #eee'
                              }}
                            >
                              {log.details}
                            </Box>
                          ) : (
                            'N/A'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalEmailLogs}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </TabPanel>
        
        <TabPanel value={activeTab} index={1}>
          <Box component="form" onSubmit={handleAuthFilterSubmit} sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="auth-status-filter-label">Status Filter</InputLabel>
              <Select
                labelId="auth-status-filter-label"
                id="auth-status-filter"
                value={authStatusFilter}
                onChange={handleAuthStatusFilterChange}
                label="Status Filter"
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Username"
              type="text"
              size="small"
              value={authUsernameFilter}
              onChange={handleAuthUsernameFilterChange}
              placeholder="Filter by username"
              sx={{ width: 180 }}
            />
            
            <Button 
              variant="outlined" 
              type="submit"
              startIcon={<RefreshOutlined />}
            >
              Apply Filters
            </Button>
          </Box>
          
          {authLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : authLogs.length === 0 ? (
            <Typography variant="body1" sx={{ textAlign: 'center', p: 3 }}>
              No authentication logs found.
            </Typography>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table sx={{ minWidth: 650 }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Username</TableCell>
                      <TableCell>IP Address</TableCell>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {authLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{log.id}</TableCell>
                        <TableCell>
                          <Chip 
                            label={log.status} 
                            color={log.status === 'success' ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{log.username}</TableCell>
                        <TableCell>{log.ip_address}</TableCell>
                        <TableCell>{new Date(log.attempt_time).toLocaleString()}</TableCell>
                        <TableCell>
                          {log.reason || 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalAuthLogs}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </TabPanel>
        
        <TabPanel value={activeTab} index={2}>
          <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
            <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="log-type-label">Log Type</InputLabel>
              <Select
                labelId="log-type-label"
                id="log-type"
                value={logType}
                onChange={handleLogTypeChange}
                label="Log Type"
              >
                <MenuItem value="notification">Notification Service</MenuItem>
                <MenuItem value="api">API Errors</MenuItem>
                <MenuItem value="all">All Logs</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Lines"
              type="number"
              size="small"
              value={logLines}
              onChange={handleChangeLogLines}
              sx={{ width: 100 }}
              InputProps={{ inputProps: { min: 10, max: 1000 } }}
            />
            
            <Button 
              variant="outlined" 
              onClick={fetchSystemLogs}
              startIcon={<RefreshOutlined />}
            >
              Refresh
            </Button>
          </Box>
          
          {systemLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : !systemLogs ? (
            <Typography variant="body1" sx={{ textAlign: 'center', p: 3 }}>
              No system logs found.
            </Typography>
          ) : (
            <Box
              component="pre"
              sx={{
                bgcolor: '#282c34',
                color: '#abb2bf',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: '600px',
                fontSize: '0.8rem',
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap'
              }}
            >
              {systemLogs.logs}
            </Box>
          )}
        </TabPanel>
      </Paper>
      
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LogViewer;